// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useEffect } from 'react';

import { useEvolvePaginatedGet, type EvolveApiParams } from '@evolvemep/api-ts';
import { Stack, Text, LoadingOverlay, ActionIcon, Card, Button } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { MobileBasePageHeader } from 'components/Mantine/MobileBasePageHeader';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { useShipmentStatuses } from 'hooks/typeCacheHooks/useShipmentStatuses';
import type { ShipmentsPageProps } from 'modules/Shop/Shipments/ShipmentsPage/ShipmentsPage';

import { useShipmentModals, ShipmentModalProvider } from '../../modalRegistries';
import { ShipmentSection } from './ShipmentSection';

const MobileShipmentsPageWrapped = ({ shipmentStates }: ShipmentsPageProps) => {
  // Determine if there are no shipments.
  // If we haven't loaded the count yet, assume there may be shipments so the shipments list still loads optimistically
  const {
    entireCount: allShipmentsCount,
    loading: loadingShipmentCount,
    fetchNextPage,
  } = useEvolvePaginatedGet('shop/shipment', {
    perPage: 1,
  });
  useEffect(() => {
    void fetchNextPage();
  }, [fetchNextPage]);
  const noShipments = allShipmentsCount === 0;

  const { facilityIds, projectIds, projects } = useGlobalFilters({
    projects: true,
    facilities: true,
  });

  const defaultParams = useMemo<EvolveApiParams<'GET_PAGINATED', 'shop/shipment'>>(
    () => ({
      facilityIds,
      projectIds,
    }),
    [facilityIds, projectIds],
  );

  const { openModal } = useShipmentModals();
  const openNewShipmentModal = () =>
    openModal('NewShipmentModal', {
      defaultValues: hasLengthExactly(projects, 1) ? { project: projects[0] } : undefined,
    });

  return (
    <Stack flex={1} mih={0} w="100%" p="md" pb={0} gap="sm">
      <MobileBasePageHeader
        title="Shipping Orders"
        rightSideComponent={
          <ActionIcon onClick={openNewShipmentModal} variant="filled" size="input-sm">
            <EvolveIcon icon="Add" size="sm" />
          </ActionIcon>
        }
      />
      <Stack flex={1} style={{ overflowY: 'auto' }} gap="md" pos="relative">
        <LoadingOverlay visible={loadingShipmentCount} />
        {noShipments ? (
          <Card bg="gray.1" radius="md" p="xl">
            <Stack gap="lg">
              <Text>Get started by creating a shipping order.</Text>
              <Button leftSection={<EvolveIcon icon="Add" size="sm" />} onClick={openNewShipmentModal}>
                Create Shipping Order
              </Button>
            </Stack>
          </Card>
        ) : (
          shipmentStates.map((state) => (
            <ShipmentSection key={state.stateId} state={state} defaultParams={defaultParams} />
          ))
        )}
      </Stack>
    </Stack>
  );
};

export const MobileShipmentsPage = () => {
  const { customProperties: customShipmentProperties } = useCustomProperties('Shipment');
  const { states, loading: loadingStates } = useShipmentStatuses();

  /**
   * We expect mobile users not to interact with draft shipments as frequently,
   * so we follow the normal sort order except with Draft at the end.
   **/
  const reorderedStates = useMemo(() => {
    if (isNil(states)) return undefined;
    return [...states.filter(({ name }) => name !== 'Draft'), ...states.filter(({ name }) => name === 'Draft')];
  }, [states]);

  if (loadingStates || isNil(reorderedStates) || isNil(customShipmentProperties)) {
    return <LoadingOverlay visible />;
  }

  return (
    <ShipmentModalProvider>
      <MobileShipmentsPageWrapped shipmentStates={reorderedStates} />
    </ShipmentModalProvider>
  );
};
